import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { decodeData, encodeData } from "../../../utils/encodeAndEncode";
import { getCourseModule } from "../../../services/adminService";
import Loader from '../../../components/admin/Loader';
import AdminPage from '../../../components/admin/AdminPage';
import { toast } from 'react-toastify';
import NoData from "../../../components/common/NoData";

function AdminCourseView() {
  const { courseId } = useParams();
  const decodedCourseId = decodeData(courseId);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [moduleData, setModuleData] = useState([]);
  const [classroomName, setClassroomName] = useState(null);

  const fetchCourseModule = async () => {
    try {
      setLoading(true);
      const response = await getCourseModule({ id: decodedCourseId });
      setClassroomName(response.course_name);
      
      if (response.success) {
        const formattedModules = response.data.modulesWithDetails.map(module => ({
          id: module.id,
          title: module.module_name,
          subtitle: module.module_desc,
          status: module.is_active ? "Active" : "Deactive",
          videos: module.video_counts,
          assessments: module.assessment_counts,
          surveys: module.survey_counts,
          documents: module.document_counts,
          assignments: module.assignment_counts,
          totalContent: module.video_counts + module.assessment_counts + 
                       module.survey_counts + module.assignment_counts + 
                       module.document_counts
        }));
        setModuleData(formattedModules);
      } else {
        setModuleData([]);
      }
    } catch (error) {
      setModuleData([]);
      toast.error('Error fetching course details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseModule();
  }, []);

  const handleModuleClick = (moduleId) => {
    const encodedModuleId = encodeData( moduleId);
    navigate(`/admin/approvalRequest/view/${courseId}/content/${encodedModuleId}`);
  };

  return (
    <AdminPage
      title="Course Details (View Only)"
      description={classroomName ? `Viewing course: ${classroomName}` : 'Course details and modules'}
      actions={
        <Link
          to="/admin/approvalRequest"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 no-underline"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Back to Approval Requests
        </Link>
      }
    >
      {loading ? (
        <div className="flex justify-center items-center min-h-[320px] rounded-2xl bg-gray-50/50 border border-gray-100">
          <Loader />
        </div>
      ) : (
        <div className="space-y-3">
          {moduleData.length === 0 ? (
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 py-12">
              <NoData message="No modules found for this course" caption="No modules" />
            </div>
          ) : (
            moduleData.map((module) => (
              <div key={module.id} className="w-full">
                <div 
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-[#4F6BFF] transition-all cursor-pointer"
                  onClick={() => handleModuleClick(module.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h5 className="mb-0 text-base font-semibold text-gray-900 truncate">{module.title}</h5>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium shrink-0 ${module.status === "Active" ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                          {module.status}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{module.subtitle}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {module.videos > 0 && (
                          <div className="flex items-center gap-1 bg-blue-50 text-blue-700 rounded-lg px-2 py-1 text-xs font-medium">
                            <Icon icon="fluent:video-24-filled" width="14" height="14" />
                            <span>{module.videos} Videos</span>
                          </div>
                        )}
                        {module.assessments > 0 && (
                          <div className="flex items-center gap-1 bg-purple-50 text-purple-700 rounded-lg px-2 py-1 text-xs font-medium">
                            <Icon icon="fluent:clipboard-task-24-filled" width="14" height="14" />
                            <span>{module.assessments} Assessments</span>
                          </div>
                        )}
                        {module.surveys > 0 && (
                          <div className="flex items-center gap-1 bg-pink-50 text-pink-700 rounded-lg px-2 py-1 text-xs font-medium">
                            <Icon icon="fluent:poll-24-filled" width="14" height="14" />
                            <span>{module.surveys} Surveys</span>
                          </div>
                        )}
                        {module.documents > 0 && (
                          <div className="flex items-center gap-1 bg-orange-50 text-orange-700 rounded-lg px-2 py-1 text-xs font-medium">
                            <Icon icon="fluent:document-24-filled" width="14" height="14" />
                            <span>{module.documents} Documents</span>
                          </div>
                        )}
                        {module.assignments > 0 && (
                          <div className="flex items-center gap-1 bg-green-50 text-green-700 rounded-lg px-2 py-1 text-xs font-medium">
                            <Icon icon="fluent:task-list-24-filled" width="14" height="14" />
                            <span>{module.assignments} Assignments</span>
                          </div>
                        )}
                        {module.totalContent === 0 && (
                          <span className="text-xs text-gray-400 italic">No content added yet</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </AdminPage>
  );
}

export default AdminCourseView;
